import { Component, OnChanges, ChangeDetectorRef, SimpleChanges, ElementRef, OnDestroy } from '@angular/core';
import { BaseRenderComponent } from 'cms/workspace/workspace-generic-classes/base-render.component';
import { CategoriesService } from 'idt-ecommerce-api/catalog/categories/categories.service';
import { StoresService } from 'idt-ecommerce-api/stores/stores.service';
import { IdtSharedRenderService } from 'render/render-shared/idt-shared-render.service';
import { RenderConfigurationService } from 'render/services/render-configuration.service';
import { iif, of } from 'rxjs';
import { switchMap, map, withLatestFrom, filter, takeUntil } from 'rxjs/operators';
import { Orientation } from 'shared/models/idt-components/idt-component.models';
import { AmandaOptions, AssetsSharedService } from 'shared/services/assets-shared.service';
import { LinksService } from 'shared/services/links.service';

import { sortCategories } from './piece-menu-utils';
import { MenuCategory, CategoryArtwork, CurrentCategoryData } from './piece-menu.models';


@Component({
  selector: 'piece-menu-render',
  templateUrl: './piece-menu-render.component.html',
  styleUrls: ['./piece-menu-render.component.scss']
})
export class PieceMenuRenderComponent
  extends BaseRenderComponent<any> // eslint-disable-line @typescript-eslint/no-explicit-any
  implements OnChanges, OnDestroy {

  public selectedCategories: MenuCategory[] = [];

  public currentCategory: CurrentCategoryData = {};
  public maxCategoryHeight: number | undefined;
  public orientation: Orientation = 'portrait';

  constructor(
    private readonly cd: ChangeDetectorRef,
    private readonly idtSharedRenderService: IdtSharedRenderService,
    private readonly renderConfigurationService: RenderConfigurationService,
    private readonly categoriesService: CategoriesService,
    private readonly storesService: StoresService,
    private readonly linksService: LinksService,
    private readonly assetsSharedService: AssetsSharedService,
    private readonly elRef: ElementRef
  ) {
    super();
  }

  public ngOnChanges(changes: SimpleChanges) {
    const storeId = this.settings.storeId;
    const catalogType = this.settings.catalogType;
    const artwork = this.settings.general.categoryArtwork;
    const overrideRootCategoryId = this.settings.general.overrideRootCategoryId;

    if (changes.settings.firstChange && ((storeId && catalogType) || overrideRootCategoryId)) {
      iif(
        () => overrideRootCategoryId,

        of([{ id: overrideRootCategoryId, name: overrideRootCategoryId } as MenuCategory]),

        this.storesService.stores().pipe(
          map((stores) => stores.find(
            (store) => store.id.toString() === storeId.toString()
          )),
          filter(isDefined),
          map((store) => store.catalogs && store.catalogs.filter(isDefined).find(
            (catalog) => `${catalog.type}` === `${catalogType}`
          )),
          filter(isDefined),
          switchMap((catalog) => this.categoriesService.categories(storeId, catalog.id))
        )
      ).pipe(
        withLatestFrom(this.idtSharedRenderService.screenOrientation$),
        takeUntil(this.clearSubscriptions$)
      ).subscribe(
        ([categories, orientation]) => {
          if (categories && (orientation === 'portrait' || orientation === 'landscape')) {
            this.selectedCategories = this.enrichCategories(categories, artwork);
            this.maxCategoryHeight = this.selectedCategories.map(
              (x) => (x?.coverImage?.landscape?.height)
            ).sort()[0];
            this.selectCategory(this.selectedCategories[0], orientation);
            this.orientation = orientation;
            this.cd.markForCheck();
          }
        }
      );
    }
  }

  public selectCategory(category: MenuCategory, orientation: Orientation) {
    const coverImage = isDefined(category) ? category.coverImage : undefined;
    const currentImage = coverImage && coverImage[orientation];

    if (!isDefined(coverImage) || !isDefined(currentImage)) {
      return;
    }

    let artwork;
    if (currentImage.image) {
      const assetsUrl = this.renderConfigurationService.config.assetsUrl;
      const akamaiOptions: AmandaOptions = {
        isWidthParamEnabled: this.renderConfigurationService.config.amandaImageVideoManagerEnabled,
        width: this.assetsSharedService.getWidth(this.elRef)
      };
      artwork = this.assetsSharedService.getAssetUrl(currentImage.image, assetsUrl, akamaiOptions);
    }

    this.currentCategory = {
      alt: coverImage.alt,
      title: coverImage.title,
      artwork,
      header: category.headerText,
      cover: category.coverText,
      footer: category.footerText
    };
  }

  public enrichCategories(categories: MenuCategory[], artworkData: CategoryArtwork[]) {
    if (!artworkData) {
      return categories;
    }
    return categories.map(
      (cat) => ({
        ...cat,
        ...artworkData.find((x) => `${x.categoryId.toString()}` === `${cat.id.toString()}`)
      })
    ).sort(sortCategories);
  }

  public getCategoryUrl(category: MenuCategory) {
    return this.linksService.getLinkUrl({
      type: 'category',
      categoryId: category.id
    });
  }

  public trackByIndex(index: number): number {
    return index;
  }

}
